import { Map } from 'immutable';
import { Point, Measurement, MeasurementKey, MeasurementType, LineMeasurement, isLineMeasurement, UnmeasurableMeasurement, isUnmeasurableMeasurement, DerivedMeasurement, isDerivedMeasurement, MeasurementCollection, isSingleValueMeasurement, MeasurementResult, LineDirection, isCategoricMeasurement } from './Measurement'


// MARK: - Resolver

/**
 * The resolve function is used to compute the final value of a measurement, handling nested derived measurements.
 * @param key The key of the measurement to resolve.
 * @param measurements The collection of measurements to resolve from.
 * @param currentDepth The current depth of the recursion. Used to prevent infinite loops.
 */
export function resolve(
    key: MeasurementKey, 
    measurements: MeasurementCollection,
    performStandardCalibration: boolean = false,
    currentDepth: number = 0
): number | 'unresolvable' {
    if(currentDepth > 10) {
        throw new Error('Recursion depth exceeded.');
    }
    
    const measurement = measurements.get(key);

    if(!measurement) {
        throw new Error(`Measurement ${key} not found.`);
    }

    if(isSingleValueMeasurement(measurement)) {
        return measurement.value;
    }

    if(isLineMeasurement(measurement)) {
        if(measurement.key === 'calibration') {
            return euclidianDistance(measurement.startPosition, measurement.endPosition);
        } else if(performStandardCalibration) {
            const calibration = measurements.get('calibration') as LineMeasurement;

            return computeLineMeasurement(measurement, calibration);
        } else {
            return computeLineMeasurement(measurement, undefined);
        }
    }

    if(isUnmeasurableMeasurement(measurement)) {
        return 'unresolvable';
    }

    if(isDerivedMeasurement(measurement)) {
        if(!areAllDependenciesAvailable(measurement, measurements)) {
            return 'unresolvable';
        }

        // filter dependencies needed and create a values map
        let values = Map<MeasurementKey, number>();

        for(const dep of measurement.dependencies) {
            const value = resolve(dep, measurements, performStandardCalibration, currentDepth + 1);

            if(value === 'unresolvable') {
                // if any dependency is unresolvable, the derived measurement must return its default value or be unresolvable itself
                return measurement.default ?? 'unresolvable';
            }

            values = values.set(dep, value);
        }

        return measurement.calculate(values);
    }

    // NOTE(gui): This is not the proper way of doing this.
    // We should NOT take categoric measurements into this function.
    // It should be renamed to `resolveNumeric` or something.
    if(isCategoricMeasurement(measurement)) {
        return 'unresolvable';
    }

    throw new Error(`Unknown measurement type "${measurement.type}".`);
}

/**
 * Computes the results of a collection of measurements. 
 * @param measurements The collection of measurements to compute results from. This usually includes both input and derived measurements.
 * @returns The results of the measurements.
 */
export function computeResults(measurements: MeasurementCollection, withCalibration: boolean = false): MeasurementResult[] {
   const keys = Array.from(measurements.keys());

    return keys.map(key => {
         const value = resolve(key, measurements, withCalibration);
    
         if(value === 'unresolvable') {
             return { key, type: 'failure', error: 'unresolvable', measurementType: measurements.get(key)!.type };
         } else {
            return { key, type: 'numeric', value, measurementType: measurements.get(key)!.type };
         }
    }) as MeasurementResult[];
}

function areAllDependenciesAvailable(measurement: DerivedMeasurement, measurements: MeasurementCollection): boolean {
    return measurement.dependencies.every(dep => measurements.has(dep));
}

function euclidianDistance(a: Point, b: Point): number {
    return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
}

function computeLineMeasurement(measurement: LineMeasurement, calibration: LineMeasurement | undefined): number {
    if(!calibration) {
        return euclidianDistance(measurement.startPosition, measurement.endPosition);
    } else {
        const distance = euclidianDistance(measurement.startPosition, measurement.endPosition);

        return distance *  calibrationScaleMultiplier(calibration, measurement.direction);
    }
}

/**
 * Return the multiplier according to the calibration and direction. 
 * This assumes the user calibrated for ONE SQUARE, which corresponds to 200 ms and 0.5 mV.
 * @param calibration The calibration measurement.
 * @param direction The direction of the measurement.
 * @returns The multiplier to apply to the measurement.
 */
function calibrationScaleMultiplier(calibration: LineMeasurement, direction: LineDirection): number {
    const calibrationDistance = Math.abs(calibration.startPosition.x - calibration.endPosition.x);

    if(direction === 'horizontal') {
        return 200 / calibrationDistance;
    } else {
        return 0.5 / calibrationDistance;
    }
}